import { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AutocompleteSearch } from "@/components/pendingShipments/autocompleteSearch";
import { CustomCotationFormData } from "@/types/forms/home/customCotationsForm.form";
import StyledPhoneInput from "@/components/styled/StyledPhoneInput";
import { CountryCode } from "libphonenumber-js";
import { GetCustomsV1Response } from "@/types/api/api-types";
import { ControlledUnifiedInput } from "../ui/customs/controlled-unified-input";
import { ControlledCustomsSelect } from "../ui/customs/controlled-customs-select";
import { countryRequiresPostalCode } from "@/lib/postal-codes.util";
import { APIProvider } from "@vis.gl/react-google-maps";
import { parseGooglePlaceAddress } from "@/lib/google-address.util";

export const InfoDelivery = ({ customs }: { customs: GetCustomsV1Response }) => {
  const { t } = useTranslation("boQuotations");
  const methods = useFormContext<CustomCotationFormData>();

  const selectedCustom = useWatch({
    control: methods.control,
    name: "customId",
  });

  const currentCountryCode = useMemo(() => {
    const countryCode = customs.find((custom) => custom.id === selectedCustom)?.flagCode;
    if (countryCode) {
      methods.setValue("deliveryInfo.countryCode", countryCode);
    }
    return countryCode;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customs, selectedCustom]);

  const countryCode = currentCountryCode ?? "";
  const showStateOrProvince = countryCode === "CA" || countryCode === "US";
  const showPostalCode = countryRequiresPostalCode(countryCode);

  const handleDeliveryCountryChange = (customId: string) => {
    methods.setValue("deliveryInfo.street", "");
    methods.setValue("deliveryInfo.postalCode", "");
    methods.setValue("deliveryInfo.city", "");
    methods.setValue("deliveryInfo.stateOrProvinceCode", "");
    methods.setValue("deliveryInfo.phone", "");
    methods.setValue("deliveryInfo.countryCode", "");

    methods.setValue("customId", customId);
    const customCountryCode = customs.find((custom) => custom.id === customId)?.flagCode;
    if (customCountryCode) {
      methods.setValue("deliveryInfo.countryCode", customCountryCode);
    }
  };

  const defaultCountry: CountryCode = (currentCountryCode || "FR") as CountryCode;

  const handlePlaceClicked = useCallback(
    (place: google.maps.places.PlaceResult | null) => {
      const parsed = parseGooglePlaceAddress(place, countryCode);
      if (!parsed) return;

      methods.setValue("deliveryInfo.street", parsed.street);
      methods.setValue("deliveryInfo.postalCode", parsed.postalCode);
      methods.setValue("deliveryInfo.city", parsed.city);

      if (showStateOrProvince) {
        methods.setValue("deliveryInfo.stateOrProvinceCode", parsed.stateOrProvinceCode);
      }
      methods.trigger([
        "deliveryInfo.street",
        "deliveryInfo.postalCode",
        "deliveryInfo.city",
        ...(showStateOrProvince ? (["deliveryInfo.stateOrProvinceCode"] as const) : []),
      ]);
    },
    [methods, showStateOrProvince, countryCode],
  );

  const handleStreetInput = (e: string) => {
    methods.setValue("deliveryInfo.street", e);
    methods.trigger(["deliveryInfo.street"]);
  };

  return (
    <APIProvider apiKey="AIzaSyDIEG5sck092TlO8WTxyEQjawmwtPMf3vw">
      <div className="shadow-basic flex w-full max-w-[800px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6">
        <div className="flex w-full flex-col items-center justify-center gap-4">
          <div className="flex w-full flex-col items-center justify-center gap-2">
            <h2 className="text-brand700 text-xl font-semibold">{t("deliveryInfo.title")}</h2>
          </div>
          <div className="flex w-full flex-col gap-2">
            <div className="flex flex-col gap-2 sm:flex-row">
              <ControlledUnifiedInput
                control={methods.control}
                name="deliveryInfo.lastName"
                label={t("deliveryInfo.lastNameLabel")}
                wrapperClassName="w-full"
              />
              <ControlledUnifiedInput
                control={methods.control}
                name="deliveryInfo.firstName"
                label={t("deliveryInfo.firstNameLabel")}
                wrapperClassName="w-full"
              />
            </div>
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.company"
              label={t("deliveryInfo.companyLabel")}
            />
            <ControlledUnifiedInput
              control={methods.control}
              name="deliveryInfo.email"
              label={t("deliveryInfo.emailLabel")}
              className="w-full"
            />
            <ControlledCustomsSelect
              customs={customs || []}
              control={methods.control}
              name="customId"
              label={t("deliveryInfo.destinationLabel")}
              setValue={(customId: string) => handleDeliveryCountryChange(customId)}
            />
            <AutocompleteSearch
              placeholder={t("deliveryInfo.streetPlaceholder")}
              onPlaceClicked={handlePlaceClicked}
              control={methods.control}
              handleStreetInput={handleStreetInput}
              formValueOverride="deliveryInfo.street"
              disabled={!selectedCustom}
              countryCode={currentCountryCode ? [currentCountryCode] : []}
            />
            <div className="flex flex-col gap-2 sm:flex-row">
              <ControlledUnifiedInput
                control={methods.control}
                name="deliveryInfo.additionalInfo"
                label={t("deliveryInfo.additionalInfoLabel")}
                disabled={!selectedCustom}
                wrapperClassName="w-full"
              />
              <ControlledUnifiedInput
                control={methods.control}
                name="deliveryInfo.additionalInfo2"
                label={t("deliveryInfo.additionalInfo2Label")}
                disabled={!selectedCustom}
                wrapperClassName="w-full"
              />
            </div>
            <div className="flex flex-col gap-2 sm:flex-row">
              {showPostalCode && (
                <ControlledUnifiedInput
                  wrapperClassName="flex-1"
                  control={methods.control}
                  name="deliveryInfo.postalCode"
                  label={t("deliveryInfo.postalCodeLabel")}
                  disabled={!selectedCustom}
                />
              )}
              <ControlledUnifiedInput
                wrapperClassName="flex-1"
                control={methods.control}
                name="deliveryInfo.city"
                label={t("deliveryInfo.cityLabel")}
                disabled={!selectedCustom}
              />
              {showStateOrProvince && (
                <ControlledUnifiedInput
                  wrapperClassName="flex-1"
                  control={methods.control}
                  name="deliveryInfo.stateOrProvinceCode"
                  label={t("deliveryInfo.stateOrProvinceLabel")}
                  disabled={!selectedCustom}
                />
              )}
            </div>
            <StyledPhoneInput
              control={methods.control}
              name="deliveryInfo.phone"
              title={t("deliveryInfo.phoneLabel")}
              disabled={!selectedCustom}
              defaultCountry={defaultCountry}
            />
          </div>
        </div>
      </div>
    </APIProvider>
  );
};
